import { useCallback, useSyncExternalStore, type AnchorHTMLAttributes, type MouseEvent, type ReactNode } from 'react'

const locationEvent = 'bubble:navigate'

function subscribe(callback: () => void) {
  window.addEventListener('popstate', callback)
  window.addEventListener(locationEvent, callback)
  return () => {
    window.removeEventListener('popstate', callback)
    window.removeEventListener(locationEvent, callback)
  }
}

function snapshot() {
  return `${window.location.pathname}${window.location.search}`
}

export function useRouter() {
  const value = useSyncExternalStore(subscribe, snapshot, () => '/')
  const queryStart = value.indexOf('?')
  const pathname = queryStart === -1 ? value : value.slice(0, queryStart)
  const search = queryStart === -1 ? '' : value.slice(queryStart)
  const navigate = useCallback((to: string, replace = false) => {
    if (replace) window.history.replaceState(null, '', to)
    else window.history.pushState(null, '', to)
    window.dispatchEvent(new Event(locationEvent))
  }, [])
  return { pathname, search, navigate }
}

interface LinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  to: string
  children: ReactNode
}

export function Link({ to, children, onClick, ...props }: LinkProps) {
  const { navigate } = useRouter()
  const follow = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event)
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    event.preventDefault()
    navigate(to)
  }
  return <a href={to} onClick={follow} {...props}>{children}</a>
}
